import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { imgUrl, track } from "@/lib/api";
import ScrollToTop from "@/components/public/ScrollToTop";

export default function Navbar({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goTop = (e) => {
    setOpen(false);
    if (window.location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const links = [
    { id: "realisations", label: "Réalisations" },
    { id: "apropos", label: "À propos" },
    { id: "clients", label: "Clients" },
  ];

  return (
    <>
    <header data-testid="public-navbar" className="fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div
          className={`flex items-center justify-between rounded-full px-3 sm:px-5 h-16 transition-all duration-300 ${
            scrolled
              ? "bg-zinc-950/70 backdrop-blur-xl border border-white/10 shadow-lg shadow-black/40"
              : "bg-transparent border border-transparent"
          }`}
        >
          <Link to="/" data-testid="nav-logo" onClick={goTop} aria-label="Black Lemon Studio - Accueil" className="flex items-center shrink-0">
            {settings?.logo_url && (
              <img
                src={imgUrl(settings.logo_url)}
                alt={settings.studio_name || "Black Lemon Studio"}
                className={`w-auto h-12 sm:h-16 lg:h-20 transition-all duration-300 ${scrolled ? "hidden" : "block"}`}
              />
            )}
            {settings?.logo_icon_url && (
              <span className={`items-center gap-2.5 ${scrolled ? "flex" : "hidden"}`}>
                <img
                  src={imgUrl(settings.logo_icon_url)}
                  alt={settings.studio_name || "Black Lemon Studio"}
                  className="w-auto transition-all duration-300 h-11 shrink-0"
                />
                <span data-testid="nav-owner-name" className="shimmer-text font-display text-sm sm:text-base leading-none tracking-tight whitespace-nowrap">
                  Julien <span className="font-black">CHAUDEAUX</span>
                </span>
              </span>
            )}
            {!settings?.logo_url && !settings?.logo_icon_url && (
              <span className="font-display text-xl tracking-tight">BLACK LEMON <span className="text-yellow-400">STUDIO</span></span>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => goSection(l.id)}
                className="relative text-sm font-medium text-zinc-300 hover:text-yellow-400 px-4 py-2 transition-colors after:absolute after:left-4 after:right-4 after:-bottom-0.5 after:h-px after:bg-yellow-400 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
              >
                {l.label}
              </button>
            ))}
            <Link data-testid="nav-blog" to="/blog" className="relative text-sm font-medium text-zinc-300 hover:text-yellow-400 px-4 py-2 transition-colors after:absolute after:left-4 after:right-4 after:-bottom-0.5 after:h-px after:bg-yellow-400 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
              Blog
            </Link>
            {settings?.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                onClick={() => track("phone_click")}
                data-testid="nav-phone"
                aria-label={`Appeler le ${settings.phone}`}
                className="hidden lg:flex items-center gap-2 text-sm font-semibold text-yellow-400 border border-yellow-400/40 rounded-full px-4 py-2 hover:bg-yellow-400 hover:text-black transition-colors ml-1"
              >
                <Phone className="w-4 h-4" /> {settings.phone}
              </a>
            )}
            <button
              data-testid="nav-contact-cta"
              onClick={() => goSection("contact")}
              className="bg-yellow-400 text-black text-sm font-bold px-5 py-2.5 rounded-full hover:bg-yellow-300 hover:shadow-[0_0_20px_rgba(255,226,64,0.4)] transition-all ml-2"
            >
              Me contacter
            </button>
          </nav>

          <div className="flex items-center gap-1 md:hidden">
            {settings?.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                onClick={() => track("phone_click")}
                data-testid="nav-phone-mobile"
                aria-label={`Appeler le ${settings.phone}`}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400 text-black"
              >
                <Phone className="w-5 h-5" />
              </a>
            )}
            <button data-testid="nav-mobile-toggle" aria-label={open ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={open} className="text-white p-2" onClick={() => setOpen(!open)}>
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden mt-2 bg-zinc-950/90 backdrop-blur-xl border border-white/10 rounded-2xl px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button key={l.id} onClick={() => goSection(l.id)} className="text-left text-zinc-200 py-2">
                {l.label}
              </button>
            ))}
            <Link to="/blog" onClick={() => setOpen(false)} className="text-zinc-200 py-2">Blog</Link>
            <div className="flex gap-4 pt-2 border-t border-white/10">
              <a href={`tel:${settings?.phone}`} onClick={() => track("phone_click")} className="flex items-center gap-2 text-yellow-400">
                <Phone size={18} /> Appeler
              </a>
              <a href={`mailto:${settings?.email}`} onClick={() => track("email_click")} className="flex items-center gap-2 text-yellow-400">
                <Mail size={18} /> Email
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
    <ScrollToTop />
    </>
  );
}
